
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ApplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  position: string;
  companyName: string;
}

export default function ApplyModal({ isOpen, onClose, position, companyName }: ApplyModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [experience, setExperience] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const formatPhone = (value: string) => {
    const nums = value.replace(/\D/g, '').slice(0, 11);
    if (nums.length <= 3) return nums;
    if (nums.length <= 7) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
    return `${nums.slice(0, 3)}-${nums.slice(3, 7)}-${nums.slice(7)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) { setSubmitMessage('이름을 입력해주세요.'); return; }
    if (!phone.trim() || phone.replace(/\D/g, '').length < 10) { setSubmitMessage('올바른 연락처를 입력해주세요.'); return; }
    if (!email.trim() || !email.includes('@')) { setSubmitMessage('올바른 이메일을 입력해주세요.'); return; }
    if (!age) { setSubmitMessage('나이를 선택해주세요.'); return; }
    if (!gender) { setSubmitMessage('성별을 선택해주세요.'); return; }
    if (!experience) { setSubmitMessage('경력을 선택해주세요.'); return; }
    if (!introduction.trim()) { setSubmitMessage('자기소개를 입력해주세요.'); return; }
    if (introduction.length > 500) { setSubmitMessage('자기소개는 500자 이내로 작성해주세요.'); return; }
    if (!agreeTerms) { setSubmitMessage('개인정보 수집에 동의해주세요.'); return; }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('position', position);
      formData.append('company', companyName);
      formData.append('name', name);
      formData.append('phone', phone);
      formData.append('email', email);
      formData.append('age', age);
      formData.append('gender', gender);
      formData.append('experience', experience);
      formData.append('introduction', introduction);

      const response = await fetch('https://readdy.ai/api/form/d6bgtsmqd308q1a091k0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setSubmitMessage('지원이 완료되었습니다!');
      } else {
        setSubmitMessage('지원 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch {
      setSubmitMessage('지원 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName('');
      setPhone('');
      setEmail('');
      setAge('');
      setGender('');
      setExperience('');
      setIntroduction('');
      setAgreeTerms(false);
      setSubmitMessage('');
      setSubmitSuccess(false);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-lg bg-dark-medium rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white">지원하기</h3>
                <p className="text-xs text-gray-500 mt-1">{companyName} · {position}</p>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            {/* Success State */}
            {submitSuccess ? (
              <div className="p-10 flex flex-col items-center text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="w-20 h-20 flex items-center justify-center rounded-full bg-emerald-500/15 mb-6"
                >
                  <i className="ri-check-double-line text-4xl text-emerald-400 w-10 h-10 flex items-center justify-center"></i>
                </motion.div>
                <h4 className="text-xl font-bold text-white mb-2">지원 완료!</h4>
                <p className="text-sm text-gray-400 mb-8 leading-relaxed">
                  {companyName}에 성공적으로 지원되었습니다.<br />
                  담당자가 확인 후 연락드릴 예정입니다.
                </p>
                <button
                  onClick={handleClose}
                  className="px-8 py-3 rounded-xl bg-gradient-neon text-white font-semibold text-sm hover:shadow-neon-purple transition-all whitespace-nowrap cursor-pointer"
                >
                  확인
                </button>
              </div>
            ) : (
              /* Form */
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="job-application-form"
                className="p-6 space-y-4 overflow-y-auto flex-1"
              >
                {/* Name & Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">이름 *</label>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="홍길동"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">연락처 *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={phone}
                      onChange={(e) => setPhone(formatPhone(e.target.value))}
                      placeholder="010-1234-5678"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple/50 transition-colors"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2">이메일 *</label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple/50 transition-colors"
                  />
                </div>

                {/* Age & Gender */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">나이 *</label>
                    <select
                      name="age"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-neon-purple/50 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-dark-medium text-gray-500">선택</option>
                      <option value="19-22" className="bg-dark-medium">19~22세</option>
                      <option value="23-25" className="bg-dark-medium">23~25세</option>
                      <option value="26-29" className="bg-dark-medium">26~29세</option>
                      <option value="30-35" className="bg-dark-medium">30~35세</option>
                      <option value="36+" className="bg-dark-medium">36세 이상</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">성별 *</label>
                    <div className="flex gap-3">
                      {['남성', '여성'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGender(g)}
                          className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer border ${
                            gender === g
                              ? 'border-neon-purple bg-neon-purple/15 text-neon-purple'
                              : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Experience */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2">경력 *</label>
                  <div className="grid grid-cols-4 gap-2">
                    {['신입', '1~2년', '3~5년', '5년 이상'].map((exp) => (
                      <button
                        key={exp}
                        type="button"
                        onClick={() => setExperience(exp)}
                        className={`py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer border whitespace-nowrap ${
                          experience === exp
                            ? 'border-neon-purple bg-neon-purple/15 text-neon-purple'
                            : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                        }`}
                      >
                        {exp}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Introduction */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2">
                    자기소개 * <span className="text-gray-600">({introduction.length}/500)</span>
                  </label>
                  <textarea
                    name="introduction"
                    value={introduction}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) setIntroduction(e.target.value);
                    }}
                    maxLength={500}
                    rows={4}
                    placeholder="간단한 자기소개와 지원 동기를 작성해주세요"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple/50 transition-colors resize-none"
                  ></textarea>
                </div>

                {/* Terms */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div
                    onClick={() => setAgreeTerms(!agreeTerms)}
                    className={`w-5 h-5 flex items-center justify-center rounded-md border mt-0.5 flex-shrink-0 transition-all ${
                      agreeTerms
                        ? 'bg-neon-purple border-neon-purple'
                        : 'border-white/20 group-hover:border-white/40'
                    }`}
                  >
                    {agreeTerms && <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>}
                  </div>
                  <span className="text-xs text-gray-400 leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. 수집된 정보는 채용 목적으로만 사용되며, 채용 완료 후 즉시 파기됩니다.
                  </span>
                </label>

                {/* Error Message */}
                {submitMessage && (
                  <p className="text-sm text-neon-pink">{submitMessage}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-xl bg-gradient-neon text-white font-bold text-sm hover:shadow-neon-purple transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      지원 중...
                    </>
                  ) : (
                    <>
                      지원서 제출하기
                      <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
